import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setLogRefreshInterval } from "@/store/reducers/settings";
import Valkyrie, {
  viArrowsRotateRight,
  viCheck,
  viClock,
  viClockPause,
  viClockSlash,
  viPause,
} from "@sippy-platform/valkyrie";

import { Box, Button, Menu, MenuItem, Stack, Tooltip } from "@mui/material";

export default function RefreshActions({
  onClick,
  isRefreshing,
  isPaused,
}: {
  onClick: () => void;
  isRefreshing: boolean;
  isPaused: boolean;
}) {
  const dispatch = useDispatch();

  const logSettings = useSelector((state) => state.settings.logs);

  // Refresh menu
  const [refreshAnchorEl, setRefreshAnchorEl] = useState(null);
  const refreshMenuOpen = Boolean(refreshAnchorEl);
  const openRefreshMenu = (event) => {
    setRefreshAnchorEl(event.currentTarget);
  };
  const closeRefreshMenu = (refreshInterval?: number) => {
    if (refreshInterval !== undefined) dispatch(setLogRefreshInterval(refreshInterval));
    setRefreshAnchorEl(null);
  };

  return (
    <>
      <Stack
        direction="row"
        sx={{
          "&>:not(:first-child)>button": {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          },
          "&>:not(:last-child)>button": {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
      >
        <Tooltip title={isRefreshing ? "Refreshing page..." : "Click to refresh"}>
          <span>
            <Button
              color="inherit"
              onClick={onClick}
              disabled={isRefreshing}
              startIcon={<Valkyrie icon={isRefreshing ? viArrowsRotateRight : viCheck} spin={isRefreshing} />}
            >
              {isRefreshing ? "Updating..." : "Up-to-date"}
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={
            isPaused
              ? "The date range doesn't include the current time, auto refresh is paused."
              : "Auto refresh option"
          }
        >
          <span>
            <Button onClick={openRefreshMenu} color="inherit" sx={{ fontSize: "1rem", px: 1.25 }}>
              <Valkyrie icon={logSettings.refreshInterval ? (isPaused ? viClockPause : viClock) : viClockSlash} />
              {!!logSettings.refreshInterval && !isPaused && (
                <Box
                  sx={(theme) => ({
                    backgroundColor: theme.vars.palette.primary.main,
                    position: "absolute",
                    bottom: 4,
                    right: 4,
                    color: "#fff",
                    fontSize: 9,
                    lineHeight: ".5rem",
                    px: 0.25,
                    pt: 0.125,
                    borderRadius: 1,
                    minWidth: 12,
                    minHeight: 12,
                  })}
                >
                  {isPaused ? <Valkyrie icon={viPause} /> : logSettings.refreshInterval}
                </Box>
              )}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Menu
        anchorEl={refreshAnchorEl}
        open={refreshMenuOpen}
        onClose={() => closeRefreshMenu(undefined)}
        slotProps={{
          paper: {
            elevation: 1,
            sx: {
              mt: 0.5,
              border: "1px solid",
              borderColor: "divider",
              minWidth: 140,
            },
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => closeRefreshMenu(0)} selected={logSettings.refreshInterval === 0}>
          Don't refresh
        </MenuItem>
        {[1, 2, 5, 10, 15].map((minutes) => (
          <MenuItem
            key={minutes}
            onClick={() => closeRefreshMenu(minutes)}
            selected={logSettings.refreshInterval === minutes}
          >
            {minutes} minute{minutes >= 2 ? "s" : ""}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
